// ── ADMIN: DASHBOARD ────────────────────────────────────────────────
// esc (shared classic script) stays a bare global — not migrated yet.
// route/viewParent/closeModal (core/routing.js, core/ui.js) are
// cross-cutting nav primitives, deliberately left as onclick="" — see
// admin-onclick-guardrail memory. cardModalForUser (billing-shared.js) is a
// real import: the a-home table button is wired via VIEW_INIT, and the one
// inside chargeCardConfirm's own modal content is attached directly
// (imperative function with control over the DOM it just wrote via
// openModal — see billing-shared.js's cardModal for the same pattern).
// chargeCardConfirm is exported: admin-accounts.js and admin-billing.js
// both reach it as a real cross-file import now (see their own headers).
import { apiFetch } from '../core/api.js';
import { DB, session } from '../core/state.js';
import { TODAY, fmtD } from '../core/format.js';
import { isParent, canEdit, activePrograms, kidInProgram, acctName, emailTo, isArchived } from '../core/domain-utils.js';
import { openModal, closeModal, toast } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh } from '../core/routing.js';
import { birthdayStar, upcomingBirthdaysForProgram } from './admin-birthdays.js';
import { emailStatusPillClass } from './admin-email-log.js';
import { setStProgram, setStPreserveFilter } from './admin-students.js';
import { showChargeError, cardModalForUser } from './billing-shared.js';

VIEWS['a-home'] = () => {
  // ── Overdue accounts ──────────────────────────────────────────────
  const overdue = DB.users.filter(u => isParent(u) && u.active).map(u => {
    const charges = DB.charges.filter(c => c.accountId === u.id && c.status === 'due');
    if (!charges.length) return null;
    const total  = charges.reduce((s,c) => s + c.amount, 0);
    const oldest = charges.reduce((m,c) => c.date < m ? c.date : m, charges[0].date);
    const days   = Math.floor((TODAY - new Date(oldest + 'T00:00')) / 86400000);
    return { u, total, oldest, days };
  }).filter(Boolean).sort((a,b) => b.days - a.days);
  const overdueTotal = overdue.reduce((s,r) => s + r.total, 0);

  // ── Active families ───────────────────────────────────────────────
  const activeFamilies = DB.users.filter(u => isParent(u) && u.active).length;

  // ── Per-program enrollment counts ────────────────────────────────
  const enrollPrograms = activePrograms().filter(p => p.isProgram);
  const counts = enrollPrograms.map(p => ({
    p, count: DB.children.filter(k => !isArchived(k) && kidInProgram(k, p.id)).length
  }));

  // ── Upcoming birthdays by program ─────────────────────────────────
  const programBdays = activePrograms().filter(p => p.isProgram).map(p => ({
    prog: p.id, label: p.name, bdays: upcomingBirthdaysForProgram(p.id)
  }));

  return `
    <div class="page-head">
      <h2 class="serif">Dashboard</h2>
    </div>

    <div class="stat-row" style="grid-template-columns:repeat(${2 + counts.length},1fr)">
      <div class="stat" onclick="route('a-reports')" style="cursor:pointer;transition:border-color 0.15s" onmouseover="this.style.borderColor='rgba(201,168,76,0.4)'" onmouseout="this.style.borderColor=''">
        <div class="lab">Outstanding balance</div>
        <div class="val" style="${overdueTotal ? 'color:var(--red)' : ''}">${fmt$(overdueTotal)}</div>
        <div class="det" style="color:var(--gold)">${overdue.length ? overdue.length + ' account' + (overdue.length > 1 ? 's' : '') + ' overdue' : 'All accounts current'} &rsaquo;</div>
      </div>
      <div class="stat" onclick="route('a-accounts')" style="cursor:pointer;transition:border-color 0.15s" onmouseover="this.style.borderColor='rgba(201,168,76,0.4)'" onmouseout="this.style.borderColor=''">
        <div class="lab">Active families</div>
        <div class="val">${activeFamilies}</div>
        <div class="det" style="color:var(--gold)">parent accounts &rsaquo;</div>
      </div>
      ${counts.map(e => `
      <div class="stat" data-action="go-to-performers" data-program="${e.p.id}" style="cursor:pointer;transition:border-color 0.15s" onmouseover="this.style.borderColor='rgba(201,168,76,0.4)'" onmouseout="this.style.borderColor=''">
        <div class="lab">${esc(e.p.name)}</div>
        <div class="val">${e.count}</div>
        <div class="det" style="color:var(--gold)">${e.count === 1 ? 'performer' : 'performers'} &rsaquo;</div>
      </div>`).join('')}
    </div>

    ${overdue.length ? `
    <div class="card" style="margin-bottom:18px">
      <div class="card-hd">Overdue accounts</div>
      <div style="max-height:260px;overflow-y:auto">
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <thead><tr>
          <th style="text-align:left;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);font-weight:500;padding:8px 12px 8px 0;border-bottom:1px solid var(--border)">Family</th>
          <th style="text-align:left;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);font-weight:500;padding:8px 8px;border-bottom:1px solid var(--border)">Performers</th>
          <th style="text-align:left;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);font-weight:500;padding:8px 8px;border-bottom:1px solid var(--border)">Balance</th>
          <th style="border-bottom:1px solid var(--border)"></th>
        </tr></thead>
        <tbody>
          ${overdue.map(r => `<tr>
            <td style="padding:10px 12px 10px 0;border-bottom:1px solid var(--border-soft);vertical-align:middle">
              <button class="tbl-link" onclick="viewParent('${r.u.id}')">${esc(r.u.firstName)} ${esc(r.u.lastName)}</button>
            </td>
            <td style="padding:10px 8px;border-bottom:1px solid var(--border-soft);vertical-align:middle;color:var(--muted)">${DB.children.filter(c => c.accountId === r.u.id && !isArchived(c)).map(k => esc(k.name.split(' ')[0])).join(', ') || '—'}</td>
            <td style="padding:10px 8px;border-bottom:1px solid var(--border-soft);vertical-align:middle;font-family:'Libre Baskerville',serif;color:var(--red);white-space:nowrap">${fmt$(r.total)}</td>
            <td style="padding:10px 0 10px 8px;border-bottom:1px solid var(--border-soft);vertical-align:middle;white-space:nowrap;text-align:right">
              <button class="btn-ghost btn-sm" data-action="charge-card-confirm" data-account="${r.u.id}" data-name="${esc(r.u.firstName + ' ' + r.u.lastName)}" data-total="${r.total}">Charge card</button>
              <button class="btn-ghost btn-sm" data-action="card-modal-for-user" data-account="${r.u.id}" style="margin-left:4px">Update card</button>
            </td>
          </tr>`).join('')}
        </tbody>
      </table></div>
    </div>` : ''}

    <div class="grid-2">
      ${programBdays.map(({prog, label, bdays}) => `
      <div class="card">
        <div class="card-hd">${esc(label)} birthdays</div>
        ${bdays.length ? bdays.map((b, i) => `
          <div style="display:flex;align-items:center;gap:10px;padding:6px 0;${i < bdays.length - 1 ? 'border-bottom:1px solid var(--border-soft)' : ''}">
            ${birthdayStar()}
            <span style="flex:1;min-width:0;font-size:13px;color:var(--cream)">${esc(b.name)}${b.isHalf ? ' <span style="font-size:11px;color:var(--gold-light)">½ birthday</span>' : ''}</span>
            <span style="font-size:12px;color:var(--muted);flex-shrink:0">${b.dayName.slice(0,3)} ${b.dateStr}${b.isHalf ? '' : ' · turning ' + b.turningAge}</span>
          </div>`).join('') : `<p class="empty">No birthdays this week.</p>`}
      </div>`).join('')}
    </div>
    ${canEdit(session) ? `<div class="card" style="margin-top:18px">
      <div class="card-hd">Email log</div>
      ${DB.emailLog.length ? `<div class="tbl-wrap"><table>
        <tr><th>Date</th><th>Type</th><th>Account</th><th>Email</th><th>Subject</th><th>Status</th></tr>
        ${DB.emailLog.slice().sort((a,b) => b.date.localeCompare(a.date)).slice(0, 5).map(e => `<tr>
          <td style="white-space:nowrap">${fmtD(e.date)}</td>
          <td><span class="pill pill-off" style="font-size:10px">${e.type.replace(/_/g,' ')}</span></td>
          <td>${e.accountId ? esc(acctName(e.accountId)) : '—'}</td>
          <td style="font-size:12px;color:var(--muted)">${esc(emailTo(e))}</td>
          <td style="font-size:13px">${esc(e.subject)}</td>
          <td><span class="pill ${emailStatusPillClass(e.status)}">${e.status}</span></td>
        </tr>`).join('')}
      </table></div>
      ${DB.emailLog.length > 5 ? `<button onclick="route('a-email-log')" style="margin-top:14px;width:100%;padding:8px;background:transparent;border:1px dashed rgba(201,168,76,0.3);border-radius:7px;color:var(--gold);font-size:13px;font-family:inherit;cursor:pointer">View all email activity &rsaquo;</button>` : ''}` : '<p class="empty">No automated emails sent yet.</p>'}
    </div>` : ''}`;
};

VIEW_INIT['a-home'] = () => {
  document.querySelectorAll('[data-action="card-modal-for-user"]').forEach(btn => {
    btn.addEventListener('click', () => cardModalForUser(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="go-to-performers"]').forEach(el => {
    el.addEventListener('click', () => goToPerformers(el.dataset.program));
  });
  document.querySelectorAll('[data-action="charge-card-confirm"]').forEach(btn => {
    btn.addEventListener('click', () => chargeCardConfirm(btn.dataset.account, btn.dataset.name, Number(btn.dataset.total)));
  });
};

function goToPerformers(programId) {
  // stProgram/stPreserveFilter live in views/admin-students.js as module
  // state; these setters mirror the reassignment the way core/state.js's
  // setters do (a bare assignment here would silently create a stray window
  // property instead of updating the module's real binding).
  setStProgram(programId);
  setStPreserveFilter(true);
  route('a-students');
}
export function chargeCardConfirm(accountId, name, total) {
  const u = DB.users.find(x => x.id === accountId);
  const card = u && u.card ? `${esc(u.card.brand)} ending in ${esc(u.card.last4)}` : null;
  openModal(`
    <h3>Charge card</h3>
    <p style="font-size:14px;color:var(--cream);margin-bottom:6px">${esc(name)}</p>
    ${card
      ? `<p class="card-intro">This will charge <b style="color:var(--cream)">${fmt$(total)}</b> to the ${card} on file for all outstanding balances.</p>`
      : `<p style="font-size:13px;color:var(--red);margin-bottom:18px">No card on file for this account. <button id="chargeConfirmAddCardBtn" style="color:var(--gold);background:none;border:none;cursor:pointer;font-size:inherit;font-family:inherit;padding:0;text-decoration:underline">Add a card</button> before charging.</p>`}
    <div style="display:flex;gap:10px">
      ${card ? `<button class="btn" style="flex:1" id="chargeCardNowBtn" data-label="Confirm — charge ${fmt$(total)}">Confirm — charge ${fmt$(total)}</button>` : ''}
      <button class="btn-ghost ${card ? '' : 'btn'}" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  const addCardBtn = document.getElementById('chargeConfirmAddCardBtn');
  if (addCardBtn) addCardBtn.addEventListener('click', () => { closeModal(); cardModalForUser(accountId); });
  const nowBtn = document.getElementById('chargeCardNowBtn');
  if (nowBtn) nowBtn.addEventListener('click', () => chargeCardNow(accountId));
}
async function chargeCardNow(accountId) {
  const btn = document.querySelector('#modal .btn:not(.btn-ghost)');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Processing…'; }
  try {
    const result = await apiFetch('/charges/charge-card', { method: 'POST', body: JSON.stringify({ accountId }) });
    DB.charges.filter(c => c.accountId === accountId && c.status === 'due').forEach(c => c.status = 'paid');
    closeModal();
    refresh();
    toast('Card charged successfully');
  } catch (e) {
    showChargeError(e.message || 'The charge was declined. No payment was taken.');
  }
}
